import { PageHeader } from '../../components/PageHeader';
import { useQuota } from '../../components/quota/useQuota';
import { plannedPostsApi } from '../calendar/calendarApi';
import { addDays, parseIsoDate, todayIso } from '../calendar/calendarDates';
import { useRequestHistory } from '../history/useRequestHistory';
import { useCollection } from '../knowledge-base/useCollection';
import { useAccount } from '../onboarding/useAccount';
import { FeedPanel } from './FeedPanel';
import { HomeStats } from './HomeStats';
import { NeedsYou } from './NeedsYou';
import { NextSevenDays } from './NextSevenDays';
import { NEEDS_YOU_PAGE_SIZE, NEEDS_YOU_STATUSES, needsYouItems } from './needsYouItems';
import { PipelineCard } from './PipelineCard';
import { QuickStart } from './QuickStart';
import { ReadinessLine } from './ReadinessLine';
import { RecentPosts } from './RecentPosts';
import { ThisWeek } from './ThisWeek';
import { useHomeStats } from './useHomeStats';
import { plansThisWeek, THIS_WEEK_DAYS } from './weekPlans';

/** Recent posts on Home: two rows of three. */
const RECENT_POSTS = 6;

/**
 * Home: the work, not the setup. Four numbers, this month's pipeline and the
 * next seven days, then a quick start, what is waiting on the user,
 * the latest posts as pictures, the week ahead and the top of the feed, then a
 * one-line readiness nudge that disappears once the knowledge base is full.
 *
 * Every read here is a list or count call; nothing on the page creates a request or
 * spends a generation — the quick start hands a topic to the brief, and the
 * brief is where the quota goes.
 */
export function DashboardPage() {
  const { business } = useAccount();
  const businessId = business?.id ?? null;
  const { quota } = useQuota();
  const today = todayIso();

  const waiting = useRequestHistory(businessId, {
    status: NEEDS_YOU_STATUSES,
    pageSize: NEEDS_YOU_PAGE_SIZE,
  });
  const recent = useRequestHistory(businessId, { pageSize: RECENT_POSTS });
  const plans = useCollection(plannedPostsApi, businessId, 'planned_posts');

  const needs = needsYouItems(waiting.requests, plans.items);
  const isNeedsLoading = waiting.isLoading || plans.isLoading;
  const week = plansThisWeek(plans.items, today);

  const homeStats = useHomeStats(businessId);
  const lastDay = parseIsoDate(addDays(today, THIS_WEEK_DAYS - 1)).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  // The viewer's own date; the numbers live in the tiles below.
  const dateLine = parseIsoDate(today).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const needsError = waiting.error ?? plans.error;

  return (
    <main className="page page--wide">
      <PageHeader
        section="Home"
        title="Home"
        lede={`${dateLine}. Here is what needs you and what is coming up.`}
      />

      <div className="home">
        {businessId ? (
          <HomeStats
            waiting={
              isNeedsLoading || needsError ? null : `${needs.length}${waiting.hasMore ? '+' : ''}`
            }
            isWaiting={!isNeedsLoading && !needsError && needs.length > 0}
            inProgress={homeStats.stats?.in_progress ?? null}
            plannedThisWeek={plans.isLoading || plans.error ? null : week.length}
            through={lastDay}
            quota={quota}
          />
        ) : null}

        {/* The page's one action, above the charts so it is in the first screen. */}
        <QuickStart brandPlatforms={business?.platforms ?? []} />

        {businessId ? (
          <div className="home__charts">
            <PipelineCard
              runs={homeStats.stats?.runs ?? null}
              isLoading={homeStats.isLoading}
              error={homeStats.error}
            />
            <NextSevenDays
              plans={week}
              today={today}
              isLoading={plans.isLoading}
              error={plans.error}
            />
          </div>
        ) : null}

        {businessId ? (
          <>
            <NeedsYou
              items={needs}
              isCapped={waiting.hasMore}
              isLoading={isNeedsLoading}
              error={needsError}
              onRetry={() => {
                waiting.retry();
                plans.refresh();
              }}
            />

            <div className="home__grid">
              <RecentPosts
                businessId={businessId}
                requests={recent.requests}
                isLoading={recent.isLoading}
                error={recent.error}
                onRetry={recent.retry}
              />
              <div className="home__side">
                <ThisWeek
                  plans={week}
                  today={today}
                  isLoading={plans.isLoading}
                  error={plans.error}
                />
                <FeedPanel businessId={businessId} />
              </div>
            </div>

            <ReadinessLine businessId={businessId} />
          </>
        ) : null}
      </div>
    </main>
  );
}
